import { useState, useEffect } from 'react';
import { Lock, Users, Heart, MapPin, Globe, Wifi, Clock, Eye, Shield, Trash2, AlertTriangle, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { cityConfig } from '@/config/cities';

const API = process.env.REACT_APP_BACKEND_URL;

const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const Admin = () => {
  const [password, setPassword] = useState('');
  const [token, setToken] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('moderation');
  const [visitors, setVisitors] = useState(null);
  const [hearts, setHearts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleted, setDeleted] = useState(new Set());

  const handleLogin = async () => {
    setError('');
    try {
      const resp = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!resp.ok) { setError('Mot de passe incorrect'); return; }
      const data = await resp.json();
      setToken(data.token);
    } catch (e) { setError('Erreur de connexion'); }
  };

  useEffect(() => {
    if (!token) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [vResp, hResp] = await Promise.all([
          fetch(`${API}/api/admin/visitors?token=${token}&days=7`),
          fetch(`${API}/api/admin/hearts?token=${token}`),
        ]);
        if (vResp.ok) setVisitors(await vResp.json());
        if (hResp.ok) setHearts(await hResp.json());
      } catch (e) { console.error('Fetch error:', e); }
      setLoading(false);
    };
    fetchData();
  }, [token]);

  const handleDelete = async (heartNumber) => {
    if (!window.confirm(`Supprimer et bannir le cœur #${heartNumber} ?`)) return;
    setDeleting(heartNumber);
    try {
      const resp = await fetch(`${API}/api/admin/ban-and-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: 'qargo.art', heart_numbers: [heartNumber] }),
      });
      if (resp.ok) {
        setDeleted(prev => new Set([...prev, heartNumber]));
      }
    } catch (e) { console.error('Delete error:', e); }
    setDeleting(null);
  };

  const handleDeleteOnly = async (heartNumber) => {
    if (!window.confirm(`Supprimer le cœur #${heartNumber} (sans bannir) ?`)) return;
    setDeleting(heartNumber);
    try {
      const resp = await fetch(`${API}/api/admin/delete-heart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: 'qargo.art', heart_number: heartNumber }),
      });
      if (resp.ok) {
        setDeleted(prev => new Set([...prev, heartNumber]));
      }
    } catch (e) { console.error('Delete error:', e); }
    setDeleting(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(to bottom, #0A0F1E, #080B18)' }}>
        <div className="w-full max-w-xs space-y-6 text-center">
          <Shield className="w-12 h-12 text-[#E63946] mx-auto" />
          <h1 className="text-white font-display text-xl font-bold">Admin</h1>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            placeholder="Mot de passe"
            className="w-full bg-white/5 border border-white/10 text-white px-4 py-3 rounded text-sm focus:outline-none focus:border-[#E63946]"
            data-testid="admin-password"
            autoFocus
          />
          {error && <p className="text-[#E63946] text-xs">{error}</p>}
          <button onClick={handleLogin} className="w-full bg-[#E63946] text-white font-bold py-3 rounded text-sm" data-testid="admin-login-btn">
            Accéder
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-3 py-4" style={{ background: 'linear-gradient(to bottom, #0A0F1E, #080B18)' }} data-testid="admin-dashboard">
      <h1 className="text-white font-display text-lg font-bold mb-4 text-center">Admin — Hearts in {cityConfig.name}</h1>

      {/* Stats cards */}
      {visitors && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="bg-white/5 border border-white/10 rounded p-3 text-center">
            <Eye className="w-4 h-4 text-[#E63946] mx-auto mb-1" />
            <div className="text-white font-bold text-lg">{visitors.total_visits}</div>
            <div className="text-white/40 text-[10px]">Visites 7j</div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded p-3 text-center">
            <Users className="w-4 h-4 text-[#E63946] mx-auto mb-1" />
            <div className="text-white font-bold text-lg">{visitors.unique_visitors}</div>
            <div className="text-white/40 text-[10px]">Uniques</div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded p-3 text-center">
            <Heart className="w-4 h-4 text-[#E63946] mx-auto mb-1" />
            <div className="text-white font-bold text-lg">{hearts ? hearts.length : 0}</div>
            <div className="text-white/40 text-[10px]">Cœurs</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1.5 mb-4">
        <button onClick={() => setTab('moderation')} className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded ${tab === 'moderation' ? 'bg-[#E63946] text-white' : 'bg-white/5 text-white/40'}`}>
          Modération
        </button>
        <button onClick={() => setTab('visitors')} className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded ${tab === 'visitors' ? 'bg-[#E63946] text-white' : 'bg-white/5 text-white/40'}`}>
          Connexions
        </button>
        <button onClick={() => setTab('hearts')} className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded ${tab === 'hearts' ? 'bg-[#E63946] text-white' : 'bg-white/5 text-white/40'}`}>
          Cœurs
        </button>
      </div>

      {loading && <p className="text-white/40 text-sm text-center py-8">Chargement...</p>}

      {/* Moderation tab — photo grid */}
      {tab === 'moderation' && hearts && (
        <div>
          <p className="text-white/40 text-[10px] uppercase tracking-widest mb-3 px-1">
            {hearts.length} soumissions — cliquer pour supprimer les faux
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            {hearts.map((h) => {
              const isDeleted = deleted.has(h.heart_number);
              const isDeleting = deleting === h.heart_number;
              const photoUrl = h.close_photo_path ? `${API}/api/files/${h.close_photo_path}` : null;

              if (isDeleted) {
                return (
                  <div key={h.heart_number} className="relative aspect-square bg-green-900/20 border border-green-500/30 flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-500" />
                  </div>
                );
              }

              return (
                <div key={h.heart_number} className="relative aspect-square bg-[#141416] border border-[#1D1D21] overflow-hidden group">
                  {photoUrl ? (
                    <img src={photoUrl} alt={`#${h.heart_number}`} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-[#1A1A1E]">
                      <AlertTriangle className="w-5 h-5 text-yellow-500/50" />
                    </div>
                  )}

                  {/* Info overlay */}
                  <div className="absolute bottom-0 left-0 right-0 py-1 px-1.5 flex items-center justify-between"
                    style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.9))' }}>
                    <div>
                      <span className="text-white font-bold text-[11px]">#{h.heart_number}</span>
                      <span className="text-white/50 text-[9px] ml-1">{h.pseudo}</span>
                    </div>
                    {h.verification_status === 'verified' ? (
                      <Heart className="w-2.5 h-2.5 text-[#E63946]" fill="#E63946" />
                    ) : (
                      <Heart className="w-2.5 h-2.5 text-white/40" />
                    )}
                  </div>

                  {/* Action buttons on hover/tap */}
                  <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                    <button
                      onClick={() => handleDelete(h.heart_number)}
                      disabled={isDeleting}
                      className="bg-[#E63946] text-white text-[10px] font-bold px-3 py-1.5 rounded flex items-center gap-1"
                      style={{ touchAction: 'manipulation' }}
                    >
                      <Trash2 className="w-3 h-3" />
                      {isDeleting ? '...' : 'Suppr + Ban'}
                    </button>
                    <button
                      onClick={() => handleDeleteOnly(h.heart_number)}
                      disabled={isDeleting}
                      className="bg-white/10 text-white text-[10px] font-bold px-3 py-1.5 rounded flex items-center gap-1"
                      style={{ touchAction: 'manipulation' }}
                    >
                      <Trash2 className="w-3 h-3" />
                      {isDeleting ? '...' : 'Suppr seul'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Visitors tab */}
      {tab === 'visitors' && visitors && (
        <div className="space-y-1.5">
          {visitors.visitors.map((v, i) => (
            <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
              className="bg-white/[0.03] border border-white/5 rounded p-2.5 text-xs space-y-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-white/70">
                  <Globe className="w-3 h-3 text-[#E63946]" />
                  <span className="font-mono text-[11px]">{v.ip}</span>
                </div>
                <span className="text-white/30 text-[10px]">{timeAgo(v.timestamp)}</span>
              </div>
              <div className="flex items-center gap-3 text-white/40">
                {v.city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-2.5 h-2.5" />{v.city}{v.region ? `, ${v.region}` : ''}{v.country ? ` (${v.country})` : ''}
                  </span>
                )}
                {v.isp && (
                  <span className="flex items-center gap-1">
                    <Wifi className="w-2.5 h-2.5" />{v.isp}
                  </span>
                )}
              </div>
              <div className="text-white/20 text-[10px] truncate">{v.user_agent}</div>
            </motion.div>
          ))}
          {visitors.visitors.length === 0 && <p className="text-white/30 text-sm text-center py-8">Aucune visite enregistrée</p>}
        </div>
      )}

      {/* Hearts tab */}
      {tab === 'hearts' && hearts && (
        <div className="space-y-1.5">
          {hearts.map((h, i) => (
            <div key={i} className="bg-white/[0.03] border border-white/5 rounded p-2.5 flex items-center gap-3">
              <span className="text-[#E63946] font-display font-bold text-sm w-14">#{h.heart_number}</span>
              <div className="flex-1 min-w-0">
                <div className="text-white text-xs font-medium">{h.pseudo}</div>
                <div className="text-white/30 text-[10px] truncate">{h.email}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-white/40 text-[10px] flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{h.location_name || '—'}</div>
                <div className="text-white/25 text-[10px]">{timeAgo(h.discovered_at)}</div>
              </div>
            </div>
          ))}
          {hearts.length === 0 && <p className="text-white/30 text-sm text-center py-8">Aucun cœur déclaré</p>}
        </div>
      )}
    </div>
  );
};

export default Admin;
